/**
 * Derivation helpers for the consuming-cycle side of frozen specimen links.
 * `specimen.cycleId` remains the origin cycle; usage events identify a later
 * cycle that reserves, thaws, uses, transfers, or fertilizes the specimen.
 */

import type { Specimen, SpecimenUsage } from "./useSpecimens";
import { CONSUMING_SPECIMEN_USAGES } from "./frozenSpecimenUsages";

type FrozenSelectionUsage = Pick<
  SpecimenUsage,
  "usage" | "cycleId" | "hasDecisionAndConsent"
>;
type FrozenSelectionSpecimen = {
  id: Specimen["id"];
  type: Specimen["type"];
  specimenUsages: FrozenSelectionUsage[] | null;
};
type FrozenSpecimenType = FrozenSelectionSpecimen["type"];

export type FrozenCycleSelection = {
  usingFrozenSpecimens: boolean;
  specimenIds: string[];
  types: FrozenSpecimenType[];
};

export function getFrozenSpecimenSelectionForCycle(
  specimens: readonly FrozenSelectionSpecimen[] | undefined,
  cycleId: string | null | undefined,
): FrozenCycleSelection {
  if (!specimens || !cycleId) {
    return { usingFrozenSpecimens: false, specimenIds: [], types: [] };
  }

  const specimenIds: string[] = [];
  const types = new Set<FrozenSpecimenType>();

  for (const specimen of specimens) {
    const linked = (specimen.specimenUsages ?? []).some(
      (usage) =>
        usage.cycleId === cycleId && CONSUMING_SPECIMEN_USAGES.has(usage.usage),
    );
    if (!linked) continue;

    specimenIds.push(specimen.id);
    types.add(specimen.type);
  }

  return {
    usingFrozenSpecimens: specimenIds.length > 0,
    specimenIds,
    types: [...types],
  };
}
